import AdAudienceModel from './AdAudienceModel';
import { ConnectionTagModel } from './ConnectionTagModel';
import { ConnectionVariableModel } from './ConnectionVariableModel';
import { SalesForceAccount } from './SalesForceModel';
import { SendDataConfig } from './WidgetModel';

export type ConnectionModelType = 'Webhook';

export enum ConnectionRequestType {
  GET = 'get',
  POST = 'post',
}

export enum ConnectionResponseType {
  NONE = 'none',
  SAVE_ATTRIBUTES = 'save_attributes',
  MESSENGER_JSON = 'messenger_json',
}

export enum ConnectionKind {
  AUDIENCE = 'variables_updates',
  WIDGET = 'widget',
}

export type IntegrationTypes =
  | 'sendlane'
  | 'klaviyo'
  | 'default'
  | 'google_spreadsheet'
  | 'facebook_ads_audience'
  | 'mailchimp'
  | 'salesforce'
  | string
  | null;

export type WebhookKind =
  | 'google_spreadsheet'
  | 'mailchimp'
  | 'facebook_ads_audience'
  | 'zapier'
  | 'json'
  | 'klaviyo'
  | 'salesforce'
  | 'sendlane';

export type MailchimpDataType = {
  listId?: string;
  name?: string;
  emailVariableId?: number;
};

export type FormattedConnectionModel = ConnectionModel & {
  statusText: string;
  formattedUpdatedAt: string;
  formattedCreatedAt: string;
};

export type ActiveCRMSResponse = {
  crmConnections: string[];
};

type KlaviyoParams = {
  privateKey: string;
};

type KlaviyoError = {
  code?: number;
  message?: string;
};

type KlaviyoTokenErrorInfo = string | KlaviyoError | null;

type KlaviyoAccessToken = {
  errorInfo: KlaviyoTokenErrorInfo;
  status: 'initial' | 'active' | 'error';
  privateKey: string;
};

type SendlaneParams = {
  privateKey: string;
};

type SendlaneAccessToken = {
  errorInfo: string | null;
  status: 'initial' | 'active' | 'error';
  privateKey: string;
};

type KlaviyoOauthToken = {
  errorInfo: KlaviyoTokenErrorInfo;
  requiredScopes: string[];
  missingScopes: string[];
};

export const klaviyoTokenErrorMsg = (
  errorInfo: KlaviyoTokenErrorInfo,
): string => {
  let message: string | undefined;

  if (errorInfo) {
    message = typeof errorInfo === 'string' ? errorInfo : errorInfo.message;
  }
  return message || 'Private API Key error, please update it.';
};

export type ConnectionModel = {
  id: number;
  name: string;
  connectionType: ConnectionModelType;
  description: string;
  createdAt: string;
  updatedAt: string;
  pluginId: number;
  validationMessages: any;
  url: string;
  variables?: ConnectionVariableModel[];
  audienceId: number | null;
  active: boolean;
  sendDataWidgetConfig?: SendDataConfig;
  requestType: ConnectionRequestType;
  responseType: ConnectionResponseType;
  kind: ConnectionKind;
  tags: ConnectionTagModel[];
  tagsField: string | null;
  googleAccountId: number | null;
  googleAccountEmail: string | null;
  googleLookupColumn: string | null;
  googleSheetId: string | null;
  googleSheetTitle: string | null;
  googleSpreadsheetId: string | null;
  googleSpreadsheetTitle: string | null;
  integrationType: IntegrationTypes;
  connectionKind: WebhookKind;
  mailchimpAccountId: number | null;
  mailchimpAccountEmail: string | null;
  mailchimpAccessTokenId: number | null;
  data: MailchimpDataType;
  mailchimpListTitle: string | null;
  requestHeaders?: Record<any, any> | null;
  requestBody?: Record<any, any> | null;
  klaviyoParams?: KlaviyoParams;
  klaviyoAccessToken?: KlaviyoAccessToken;
  klaviyoOauthToken?: KlaviyoOauthToken;
  sendlaneParams?: SendlaneParams;
  sendlaneAccessToken?: SendlaneAccessToken;
  salesforceAccessToken?: SalesForceAccount;
  promoterIds?: number[];
  adAudienceId?: number;
  adAudience?: AdAudienceModel;
  salesforceParams?: {
    accessTokenId: number;
  };
};

export default ConnectionModel;
